import React from "react";
import { AlertTriangle, Crown, Sparkles } from "lucide-react";
import { useI18n } from "../lib/i18n";

export default function QuotaBanner({ quota, onSubscribe }) {
  const { locale } = useI18n();
  if (!quota) return null;

  // Admin : pas de bannière du tout
  if (quota.is_admin) return null;

  if (quota.is_subscribed) {
    const planLabel = quota.plan === "pro" ? "PRO" : "STANDARD";
    return (
      <div
        className="flex items-center justify-between gap-3 px-4 py-2.5 bg-primary/5 border border-primary/15 rounded-sm mb-4"
        data-testid="quota-banner-subscribed"
      >
        <div className="flex items-center gap-2.5">
          {quota.plan === "pro" ? (
            <Crown size={16} className="text-accent-dark" />
          ) : (
            <Sparkles size={16} className="text-primary" />
          )}
          <span className="text-sm text-foreground/80">
            {locale === "fr" ? "Abonnement actif —" : "Active subscription —"}
          </span>
          <span
            className={`font-mono text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 rounded-sm ${
              quota.plan === "pro"
                ? "bg-accent text-[#0D1B2A]"
                : "bg-primary text-white"
            }`}
          >
            {planLabel}
          </span>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-widest text-foreground/50">
          {locale === "fr" ? "Questions illimitées" : "Unlimited questions"}
        </span>
      </div>
    );
  }

  // Free tier: check lifetime quota
  const { questions_used_today, daily_limit, quota_remaining, lifetime_used, lifetime_limit } = quota;
  const isLifetimeExhausted = quota_remaining === 0 || (lifetime_used !== undefined && lifetime_used >= (lifetime_limit || 2));
  
  if (isLifetimeExhausted) {
    return (
      <div
        className="flex flex-col md:flex-row md:items-center gap-3 px-4 py-3 bg-[#FEE2E2] border-l-4 border-[#E76F51] rounded-r-sm mb-4"
        data-testid="quota-banner-exhausted"
      >
        <AlertTriangle size={18} className="text-[#991B1B] shrink-0" />
        <div className="flex-1 text-sm text-[#991B1B] leading-snug">
          <div className="font-semibold">
            {locale === "fr"
              ? `Limite gratuite atteinte (${lifetime_used || questions_used_today}/${lifetime_limit || daily_limit})`
              : `Free limit reached (${lifetime_used || questions_used_today}/${lifetime_limit || daily_limit})`}
          </div>
          <div className="text-xs mt-0.5">
            {locale === "fr"
              ? "Vous avez épuisé vos 2 questions gratuites à vie. Créez un compte et abonnez-vous pour continuer."
              : "You've used your 2 free questions for life. Create an account and subscribe to continue."}
          </div>
        </div>
        <button
          type="button"
          onClick={onSubscribe}
          className="inline-flex items-center gap-1.5 bg-primary text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-primary-light transition-colors"
          data-testid="quota-banner-subscribe-btn"
        >
          <Sparkles size={14} /> {locale === "fr" ? "Créer mon compte" : "Create account"}
        </button>
      </div>
    );
  }

  if (quota_remaining <= 1) {
    return (
      <div
        className="flex items-center gap-2.5 px-4 py-2.5 bg-[#FEF3C7] border border-[#FDE68A] rounded-sm mb-4"
        data-testid="quota-banner-warning"
      >
        <AlertTriangle size={14} className="text-[#92400E] shrink-0" />
        <span className="text-xs text-[#92400E]">
          {locale === "fr"
            ? `Plus que ${quota_remaining} question gratuite. Créez un compte pour un accès illimité.`
            : `Only ${quota_remaining} free question left. Create an account for unlimited access.`}
        </span>
        <button
          type="button"
          onClick={onSubscribe}
          className="ml-auto text-xs font-medium text-primary hover:underline"
          data-testid="quota-banner-upgrade-link"
        >
          {locale === "fr" ? "Voir les abonnements →" : "View plans →"}
        </button>
      </div>
    );
  }

  return (
    <div
      className="text-[11px] font-mono uppercase tracking-widest text-foreground/40 text-right mb-2"
      data-testid="quota-counter"
    >
      {questions_used_today}/{daily_limit}{" "}
      {locale === "fr" ? "questions utilisées (gratuit)" : "questions used (free)"}
    </div>
  );
}
